import {
  CORP_HOMEPAGE_SUBTITLE,
  CORP_HOMEPAGE_TITLE,
  isCorpTenant,
  useGetAllWishlistsQuery,
} from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import { Box, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  CorporateTypography,
  LandingBenefits,
  useDeviceTypes,
  usePrevious,
} from "halifax";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import {
  APPLIED_PREFERENCES,
  AUTOCOMPLETE_LOCATION_SELECTED,
  AppliedPreferencesProperties,
  AutocompleteLocationSelectedProperties,
  CALENDAR_DATES_SELECTED,
  CalendarDatesSelectedProperties,
  CallState,
  HotelEntryTypeEnum,
  LAUNCHED_MOBILE_LOCATION_SCREEN,
  LaunchedMobileLocationScreenProperties,
  RecentHotelSearch,
  Tenant,
} from "redmond";
import { ClientContext } from "../../App";
import { config } from "../../api/config";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  CASH_VALUEPROP_A,
  CASH_VALUEPROP_B,
  CASH_VALUEPROP_C,
  CASH_VALUEPROP_EXPERIMENT,
  CASH_VALUEPROP_VARIANTS,
  CONTROL,
  CUSTOMER_PROFILE_EXPERIMENT,
  CUSTOMER_PROFILE_VARIANTS,
  DEFAULT_ON,
  DESKTOP_RECENTLY_VIEWED_HOTELS,
  DESKTOP_RECENTLY_VIEWED_HOTELS_VARIANTS,
  HOTEL_COLOR_CALENDAR,
  HOTEL_COLOR_CALENDAR_VARIANTS,
  HOTEL_COLOR_CALENDAR_WITH_PRICING,
  MOBILE_RECENTLY_VIEWED_HOTELS,
  MOBILE_RECENTLY_VIEWED_HOTELS_VARIANTS,
  STAYS_HOMEPAGE,
  XMOD_V1_EXPERIMENT,
  addTrackingProperties,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  useExperiments,
} from "../../context/experiments";
import {
  PORTAL_TITLE,
  SEARCH_HOTEL_TITLE,
  SEARCH_STAYS_TITLE,
} from "../../lang/textConstants";
import { PATH_AVAILABILITY, PATH_STAYS_AVAILABILITY } from "../../utils/paths";
import { transformToStringifiedAvailabilityQuery } from "../shop/utils/queryStringHelpers";
import { ConnectedWishlistManagementModal } from "../wishlist/components/WishlistManagementModal/container";
import { HotelSearchControl } from "./components/HotelSearchControl";
import { MobileHotelSearchControlV3 } from "./components/MobileHotelSearchControlV3";
import { VacationRentalAwarenessModal } from "./components/VacationRentalAwarenessModal";
import { HotelsSearchConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";
import { parseQueryString } from "./utils/parseQueryString";

export const HotelsSearch = (props: HotelsSearchConnectorProps) => {
  const {
    fetchRewardsAccounts,
    rewardsAccounts,
    largestValueAccount,
    fetchRewardsAccountsCallState,
    fetchUserHotelPreferences,
    shouldApplyUserHotelPreferences,
    setApplyUserHotelPreferences,
    userHasSetHotelPreferences,
    userHotelPreferencesCallState,
    resetHotelAvailabilityCallState,
    listPaymentMethods,
    setInitialStayType,
  } = props;
  const { matchesMobile } = useDeviceTypes();
  const history = useHistory();
  const [recentSearches, setRecentSearches] = useState<RecentHotelSearch[]>([]);

  const { sessionInfo } = useContext(ClientContext);

  const expState = useExperiments();

  const cashValuePropVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    CASH_VALUEPROP_EXPERIMENT,
    CASH_VALUEPROP_VARIANTS
  );

  const isRecentlyViewedHotelsDesktopExperiment =
    getExperimentVariantCustomVariants(
      expState.experiments,
      DESKTOP_RECENTLY_VIEWED_HOTELS,
      DESKTOP_RECENTLY_VIEWED_HOTELS_VARIANTS
    ) !== CONTROL;

  const isRecentlyViewedHotelsMobileExperiment =
    getExperimentVariantCustomVariants(
      expState.experiments,
      MOBILE_RECENTLY_VIEWED_HOTELS,
      MOBILE_RECENTLY_VIEWED_HOTELS_VARIANTS
    ) !== CONTROL;

  const colorCalendarExperimentVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    HOTEL_COLOR_CALENDAR,
    HOTEL_COLOR_CALENDAR_VARIANTS
  );

  const customerProfileExperimentVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    CUSTOMER_PROFILE_EXPERIMENT,
    CUSTOMER_PROFILE_VARIANTS
  );

  const isCustomerProfileExperiment =
    customerProfileExperimentVariant !== CONTROL;

  const staysHomepageEnabled =
    getExperimentVariant(expState.experiments, STAYS_HOMEPAGE) === AVAILABLE;

  const hasEarnToDisplay = useMemo(
    () => !!largestValueAccount && !!largestValueAccount.earn.hotelsMultiplier,

    [largestValueAccount]
  );

  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  useGetAllWishlistsQuery(undefined, {
    skip: !isWishlistExperiment,
  });

  const getTitleToDisplay = (tenant: Tenant) => {
    if (isCorpTenant(tenant)) {
      return CORP_HOMEPAGE_TITLE;
    }

    if (
      fetchRewardsAccountsCallState === CallState.Success &&
      hasEarnToDisplay
    ) {
      return textConstants.EARN_ENHANCEMENT_TITLE(
        largestValueAccount.earn.hotelsMultiplier,
        staysHomepageEnabled ? largestValueAccount.earn.homesMultiplier : "",
        largestValueAccount.productDisplayName,
        largestValueAccount.rewardsBalance.currencyDescription ??
          largestValueAccount.rewardsBalance.currency
      );
    }

    if (
      fetchRewardsAccountsCallState === CallState.Success ||
      fetchRewardsAccountsCallState === CallState.Failed
    ) {
      if (
        largestValueAccount?.productDisplayName
          .toLowerCase()
          .includes("paradise")
      ) {
        return (
          <span className="font-regular">{textConstants.PARADISE_TITLE}</span>
        );
      }
      return (
        <span className="font-regular">
          {staysHomepageEnabled
            ? textConstants.STAYS_TITLE
            : textConstants.TITLE}
        </span>
      );
    }
    return <Skeleton className="title-loading" />;
  };

  const getSubtitleToDisplay = (tenant: Tenant) => {
    if (isCorpTenant(tenant)) {
      return (
        <CorporateTypography variant="h5" fontWeight="light">
          {CORP_HOMEPAGE_SUBTITLE}
        </CorporateTypography>
      );
    }

    if (
      fetchRewardsAccountsCallState === CallState.Success ||
      fetchRewardsAccountsCallState === CallState.Failed
    ) {
      return staysHomepageEnabled
        ? textConstants.STAYS_SUBTITLE
        : textConstants.SUBTITLE;
    }
    return <Skeleton className="subtitle-loading" />;
  };

  const onRecentSearchClick = useCallback(
    (search: RecentHotelSearch) => {
      const path = staysHomepageEnabled
        ? PATH_STAYS_AVAILABILITY
        : PATH_AVAILABILITY;

      history.push(
        `${path}${transformToStringifiedAvailabilityQuery(
          search.location,
          search.checkInDate,
          search.checkOutDate,
          search.numAdults,
          search.childrenAges.length
            ? search.childrenAges
            : Array(search.numChildren).fill(17),
          search.numRooms,
          search.numPets,
          HotelEntryTypeEnum.RECENTLY_SEARCH_AUTOCOMPLETE
        )}`
      );
    },
    [history]
  );

  useEffect(() => {
    const documentTitle = staysHomepageEnabled
      ? SEARCH_STAYS_TITLE
      : SEARCH_HOTEL_TITLE;
    document.title = documentTitle;

    return () => {
      document.title = PORTAL_TITLE;
    };
  }, [staysHomepageEnabled]);

  useEffect(() => {
    fetchRewardsAccounts(false, sessionInfo);
    listPaymentMethods();

    setTimeout(() => window.scrollTo(0, 0), 0);

    resetHotelAvailabilityCallState();
  }, []);

  useEffect(() => {
    if (isCustomerProfileExperiment) {
      fetchUserHotelPreferences();
    }
  }, [isCustomerProfileExperiment]);

  useEffect(() => {
    if (
      customerProfileExperimentVariant === DEFAULT_ON &&
      userHotelPreferencesCallState === CallState.Success &&
      userHasSetHotelPreferences
    ) {
      setApplyUserHotelPreferences(true);
    }
  }, [
    customerProfileExperimentVariant,
    userHasSetHotelPreferences,
    userHotelPreferencesCallState,
  ]);

  useEffect(() => {
    if (matchesMobile) {
      const properties: LaunchedMobileLocationScreenProperties = {
        funnel: "hotels",
        url: window.location.pathname,
      };
      trackEvent({ eventName: LAUNCHED_MOBILE_LOCATION_SCREEN, properties });
    }
  }, [matchesMobile]);

  const handleLocationSelected = useCallback((value: any) => {
    if (value?.label) {
      const properties: AutocompleteLocationSelectedProperties = {
        funnel: "hotels",
        url: window.location.pathname,
        autocomplete_value: value.label,
      };
      trackEvent({ eventName: AUTOCOMPLETE_LOCATION_SELECTED, properties });
    }
  }, []);

  const handleDatesSelected = useCallback(
    (from: Date | null, until: Date | null) => {
      if (from && until) {
        const properties: CalendarDatesSelectedProperties = {
          funnel: "hotels",
          url: window.location.pathname,
          start_date: dayjs(from).format("YYYY-MM-DD"),
          end_date: dayjs(until).format("YYYY-MM-DD"),
        };
        trackEvent({ eventName: CALENDAR_DATES_SELECTED, properties });
      }
    },
    []
  );

  useEffect(() => {
    const getRecentSearches = () => {
      try {
        const localStorageItem = localStorage.getItem(
          "recently_searched_hotels"
        );

        if (localStorageItem) {
          const parsedSearches: RecentHotelSearch[] =
            JSON.parse(localStorageItem);
          const sortedSearches = parsedSearches
            .filter((search) => !!search.location)
            .sort(
              (a, b) =>
                new Date(b.searchDate).getTime() -
                new Date(a.searchDate).getTime()
            );
          setRecentSearches(sortedSearches);
        }
      } catch (e) {
        console.error(`Failed to get recent searches from local storage: ${e}`);
      }
    };

    getRecentSearches();

    addEventListener("update_recently_searched_hotels", getRecentSearches);

    return () =>
      removeEventListener("update_recently_searched_hotels", getRecentSearches);
  }, []);

  const prevShouldApplyUserHotelPreferences = usePrevious(
    shouldApplyUserHotelPreferences
  );

  useEffect(() => {
    if (
      prevShouldApplyUserHotelPreferences === false &&
      shouldApplyUserHotelPreferences === true
    ) {
      const properties: AppliedPreferencesProperties = {
        preferences_type: "hotels",
        location: "search",
      };

      trackEvent({
        eventName: APPLIED_PREFERENCES,
        properties: addTrackingProperties(
          expState.trackingProperties,
          properties
        ),
      });
    }
  }, [shouldApplyUserHotelPreferences, prevShouldApplyUserHotelPreferences]);

  const hasOnlyCashCards = useMemo(
    () =>
      rewardsAccounts?.length > 0 &&
      rewardsAccounts.every(
        (account) => account.rewardsBalance.currency.toLowerCase() === "cash"
      ),
    [rewardsAccounts]
  );

  const benefitsVariant = useMemo(() => {
    switch (cashValuePropVariant) {
      case CONTROL:
      default:
        return [];
      case CASH_VALUEPROP_A:
        return textConstants.CASH_CARD_BENEFITS.VARIANT_A;
      case CASH_VALUEPROP_B:
        return textConstants.CASH_CARD_BENEFITS.VARIANT_B;
      case CASH_VALUEPROP_C:
        return textConstants.CASH_CARD_BENEFITS.VARIANT_C;
    }
  }, [cashValuePropVariant]);

  const parsedQueryString = parseQueryString(history);
  const stayType = parsedQueryString?.stayType;

  useEffect(() => {
    setInitialStayType(stayType);
  }, [stayType]);

  return (
    <>
      {matchesMobile ? (
        <Box className="mobile-hotel-search-root">
          <Box className="hotel-search-container-mobile">
            <MobileHotelSearchControlV3
              recentSearches={
                isRecentlyViewedHotelsMobileExperiment
                  ? recentSearches
                  : undefined
              }
              onRecentSearchClick={onRecentSearchClick}
              onSelectLocation={handleLocationSelected}
              onSelectDates={handleDatesSelected}
              showCalendarPricingColors={
                colorCalendarExperimentVariant !== CONTROL
              }
              showCalendarPricing={
                colorCalendarExperimentVariant ===
                HOTEL_COLOR_CALENDAR_WITH_PRICING
              }
              isCustomerProfileExperiment={isCustomerProfileExperiment}
            />
          </Box>
        </Box>
      ) : (
        <Box
          className={clsx("hotel-search-root", "multiroom", {
            xmod: isXmodRedesign,
          })}
        >
          <Box
            className={clsx("hotel-search-container-desktop", config.TENANT)}
          >
            <Box className={clsx("hotel-search-titles-and-fields-wrapper")}>
              {isCorpTenant(config.TENANT) ? (
                <CorporateTypography variant="h1" className="search-title">
                  {CORP_HOMEPAGE_TITLE}
                </CorporateTypography>
              ) : (
                <Typography variant="h1" className="search-title">
                  {getTitleToDisplay(config.TENANT)}
                </Typography>
              )}
              <Typography className="search-subtitle">
                {getSubtitleToDisplay(config.TENANT)}
              </Typography>
              <HotelSearchControl
                saveDatesOnClose
                showTotalTravelers
                recentSearches={
                  isRecentlyViewedHotelsDesktopExperiment
                    ? recentSearches
                    : undefined
                }
                onRecentSearchClick={onRecentSearchClick}
                showPreferencesBanner
              />
            </Box>
          </Box>
          {cashValuePropVariant !== CONTROL && hasOnlyCashCards && (
            <LandingBenefits
              title={textConstants.CASH_CARD_BENEFITS_TITLE(
                rewardsAccounts?.[0]?.productDisplayName
              )}
              benefits={benefitsVariant}
            />
          )}
        </Box>
      )}
      <VacationRentalAwarenessModal />
      <ConnectedWishlistManagementModal
        history={history}
        entryType="stays_search"
        mobileModalFullscreen={true}
      />
    </>
  );
};
